/**
 * Small imperative helper that turns a `position: fixed` element into a
 * draggable (and optionally resizable) overlay window. Used by both the
 * action-trace overlay and the chunk-render-debug skeleton overlay.
 *
 * Persists `top`/`left`/`width`/`height` to localStorage under the supplied
 * key so the user's chosen placement and size stick across reloads.
 *
 * Implementation notes:
 *
 * - Doesn't depend on React; works for any HTMLElement.
 * - Adds a small drag handle bar at the top of the element. The whole bar
 *   is the drag affordance — clicks on bar dispatch pointer-move tracking.
 * - For resize, uses CSS `resize: both`. We persist the post-resize size on
 *   `mouseup` (no `ResizeObserver` needed — keeps the implementation tiny).
 */

export interface WindowOptions {
  /** localStorage key prefix used to persist `top`/`left`/`width`/`height`. */
  storageKey: string;
  /** If true, attach a CSS resize handle in the bottom-right corner. */
  resizable?: boolean;
  /** Optional human-friendly title shown in the drag handle bar. */
  title?: string;
}

interface PersistedRect {
  top?: number;
  left?: number;
  width?: number;
  height?: number;
}

function readRect(storageKey: string): PersistedRect {
  try {
    const raw = globalThis.localStorage?.getItem(storageKey);
    if (!raw) {
      return {};
    }
    return JSON.parse(raw) as PersistedRect;
  } catch {
    return {};
  }
}

function writeRect(storageKey: string, rect: PersistedRect): void {
  try {
    globalThis.localStorage?.setItem(storageKey, JSON.stringify(rect));
  } catch {
    // Best-effort.
  }
}

function clampToViewport(value: number, _size: number, viewportSize: number): number {
  // Keep at least 40px of the element on screen so the drag bar is always
  // reachable even if the user resizes the window after positioning.
  const margin = 40;
  return Math.min(Math.max(0, value), Math.max(0, viewportSize - margin));
}

export interface MovableWindowHandle {
  /** Returns the drag handle element so callers can mutate its title text. */
  getHandle: () => HTMLElement;
  /** Releases listeners and removes the handle from the element. */
  dispose: () => void;
}

/**
 * Wraps `el` so it becomes draggable (and optionally resizable). Idempotent
 * per element — calling twice returns the same handle and doesn't add
 * duplicate handles or listeners.
 */
export function makeMovableResizable(
  el: HTMLElement,
  options: WindowOptions
): MovableWindowHandle {
  const existing = (el as any).__movableHandle as MovableWindowHandle | undefined;
  if (existing) {
    return existing;
  }

  const { storageKey, resizable = false, title = '' } = options;

  // Restore persisted rect.
  const rect = readRect(storageKey);
  if (typeof rect.top === 'number') {
    el.style.top = `${clampToViewport(rect.top, 0, globalThis.innerHeight ?? 800)}px`;
    el.style.bottom = 'auto';
  }
  if (typeof rect.left === 'number') {
    el.style.left = `${clampToViewport(rect.left, 0, globalThis.innerWidth ?? 1200)}px`;
    el.style.right = 'auto';
  }
  if (resizable) {
    if (typeof rect.width === 'number') {
      el.style.width = `${rect.width}px`;
      // Allow user to shrink below the original maxWidth.
      el.style.maxWidth = 'none';
    }
    if (typeof rect.height === 'number') {
      el.style.height = `${rect.height}px`;
    }
    el.style.resize = 'both';
    el.style.overflow = 'auto';
    el.style.minWidth = '180px';
    el.style.minHeight = '60px';
  }

  // Build drag handle bar.
  const handle = document.createElement('div');
  handle.setAttribute('data-perf-tracing-drag-handle', '');
  Object.assign(handle.style, {
    cursor: 'move',
    padding: '4px 8px',
    margin: '-12px -16px 8px -16px',
    background: 'rgba(255,255,255,0.08)',
    borderTopLeftRadius: '8px',
    borderTopRightRadius: '8px',
    fontFamily: 'monospace',
    fontSize: '11px',
    color: '#9ce',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    userSelect: 'none',
    pointerEvents: 'auto',
  } satisfies Partial<CSSStyleDeclaration>);
  handle.textContent = title || 'drag';

  el.style.pointerEvents = 'auto';
  if (el.firstChild) {
    el.insertBefore(handle, el.firstChild);
  } else {
    el.appendChild(handle);
  }

  // Drag implementation: track pointer offset on mousedown, move element on
  // pointermove, persist on pointerup. Uses pointer events to handle stylus
  // and touch as well as mouse.
  let dragOffsetX = 0;
  let dragOffsetY = 0;
  let dragging = false;

  function onPointerDown(e: PointerEvent) {
    dragging = true;
    const rectNow = el.getBoundingClientRect();
    dragOffsetX = e.clientX - rectNow.left;
    dragOffsetY = e.clientY - rectNow.top;
    handle.setPointerCapture(e.pointerId);
  }

  function onPointerMove(e: PointerEvent) {
    if (!dragging) {
      return;
    }
    const left = clampToViewport(
      e.clientX - dragOffsetX,
      el.offsetWidth,
      globalThis.innerWidth ?? 1200
    );
    const top = clampToViewport(
      e.clientY - dragOffsetY,
      el.offsetHeight,
      globalThis.innerHeight ?? 800
    );
    el.style.left = `${left}px`;
    el.style.top = `${top}px`;
    el.style.right = 'auto';
    el.style.bottom = 'auto';
  }

  function persistRect() {
    const r = el.getBoundingClientRect();
    const current = readRect(storageKey);
    writeRect(storageKey, {
      ...current,
      top: Math.round(r.top),
      left: Math.round(r.left),
      ...(resizable ? { width: Math.round(r.width), height: Math.round(r.height) } : {}),
    });
  }

  function onPointerUp(e: PointerEvent) {
    if (!dragging) {
      return;
    }
    dragging = false;
    try {
      handle.releasePointerCapture(e.pointerId);
    } catch {
      // Already released.
    }
    persistRect();
  }

  handle.addEventListener('pointerdown', onPointerDown);
  handle.addEventListener('pointermove', onPointerMove);
  handle.addEventListener('pointerup', onPointerUp);
  handle.addEventListener('pointercancel', onPointerUp);

  // For resize: capture size on mouseup anywhere on the element.
  let resizeMouseUp: ((e: MouseEvent) => void) | null = null;
  if (resizable) {
    resizeMouseUp = () => persistRect();
    el.addEventListener('mouseup', resizeMouseUp);
  }

  const handleObj: MovableWindowHandle = {
    getHandle: () => handle,
    dispose: () => {
      handle.removeEventListener('pointerdown', onPointerDown);
      handle.removeEventListener('pointermove', onPointerMove);
      handle.removeEventListener('pointerup', onPointerUp);
      handle.removeEventListener('pointercancel', onPointerUp);
      if (resizeMouseUp) {
        el.removeEventListener('mouseup', resizeMouseUp);
      }
      handle.remove();
      delete (el as any).__movableHandle;
    },
  };
  (el as any).__movableHandle = handleObj;
  return handleObj;
}
